import { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import {
	AreaChart, Area, XAxis, YAxis, CartesianGrid, ReferenceLine,
} from 'recharts';
import {
	BarChart3, Search, ChevronDown, TrendingUp, TrendingDown,
	Target, Activity, Loader2, ArrowUpDown, ArrowUp, ArrowDown,
	Scale, CalendarDays, Gauge,
} from 'lucide-react';
import { getUserPermissions, getUserAlgorithms, hasTenant } from '../utils/Permissions';
import { OPTIONS_ALGOS, INCOME_TIERS, TENANT_SMITHFIELD, TENANT_INVESTCEO } from '../utils/constants';
import { cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { Input } from './ui/Input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/Popover';
import { Checkbox } from './ui/Checkbox';
import {
	Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/Table';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from './ui/Chart';
import CalendarHeatmap from './metrics/CalendarHeatmap';
import WeeklyPnlTiles from './metrics/WeeklyPnlTiles';
import MonthlyPnlTiles from './metrics/MonthlyPnlTiles';
import '../styles/theme.css';

// First backtested options trade. Used as the default range start so the page
// opens on the full history.
const HISTORY_START_DATE = '2022-06-01';

const formatDateForInput = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const formatDollars = (value) =>
	new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

const formatPct = (value) => `${((value || 0) * 100).toFixed(1)}%`;

const formatDateTime = (value) => {
	if (!value) return '—';
	const d = new Date(value);
	if (isNaN(d.getTime())) return '—';
	return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Build cumulative P&L from daily data
const buildCumulativePnl = (dailyData) => {
	if (!dailyData?.length) return [];
	let cumulative = 0;
	return dailyData.map((d) => {
		cumulative += d.profit || 0;
		return { date: d.date, cumulative: Math.round(cumulative * 100) / 100 };
	});
};

/* ─── KPI Card ───────────────────────────────────────────── */
function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default' }) {
	return (
		<Card className="gap-4">
			<CardHeader className="pb-0">
				<CardTitle className="flex items-center text-sm text-muted-foreground">
					{Icon && <Icon className="mr-2 size-4" />}
					{title}
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div className={cn(
					'text-2xl font-bold tabular-nums',
					variant === 'profit' && 'text-green-600',
					variant === 'loss' && 'text-red-600',
					// Drawdown is expected, not a failure — amber informs without alarming.
					variant === 'caution' && 'text-amber-600',
				)}>
					{value}
				</div>
				{subtitle && <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>}
			</CardContent>
		</Card>
	);
}

/* ─── Avg Win / Loss bars ────────────────────────────────── */
// Two opposed bars sharing a scale, so the ratio between them reads at a
// glance — a number pair alone makes the reader do the division.
function WinLossBars({ avgWin, avgLoss }) {
	const win = Math.abs(avgWin || 0);
	const loss = Math.abs(avgLoss || 0);
	const max = Math.max(win, loss) || 1;

	const Row = ({ label, value, pct, tone }) => (
		<div className="flex items-center gap-2">
			<span className="w-8 shrink-0 text-[11px] text-muted-foreground">{label}</span>
			<div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
				<div
					className={cn('h-full rounded-full', tone)}
					style={{ width: `${pct}%` }}
				/>
			</div>
			<span className="w-[68px] shrink-0 text-right text-xs font-medium tabular-nums">
				{formatDollars(value)}
			</span>
		</div>
	);

	return (
		<div className="flex flex-col gap-1.5">
			<Row label="Win" value={avgWin} pct={(win / max) * 100} tone="bg-emerald-500" />
			<Row label="Loss" value={avgLoss} pct={(loss / max) * 100} tone="bg-red-500" />
		</div>
	);
}

/* ─── Annual Returns ─────────────────────────────────────── */
// Yearly P&L with the headline figures underneath. Partial years are hatched
// and labelled so a part-year total isn't read as a full one.
function AnnualReturns({ dailyPnl }) {
	const years = useMemo(() => {
		const byYear = new Map();
		for (const d of dailyPnl) {
			const year = d.date.slice(0, 4);
			const entry = byYear.get(year) || { year, pnl: 0, first: d.date, last: d.date };
			entry.pnl += d.profit || 0;
			if (d.date < entry.first) entry.first = d.date;
			if (d.date > entry.last) entry.last = d.date;
			byYear.set(year, entry);
		}
		const nowYear = String(new Date().getFullYear());
		return [...byYear.values()]
			.sort((a, b) => a.year.localeCompare(b.year))
			.map((e) => {
				// Partial if it doesn't span roughly the full trading year.
				const startsLate = e.first.slice(5) > '01-31';
				const endsEarly = e.year === nowYear || e.last.slice(5) < '12-01';
				const monthLabel = (d) =>
					new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short' });
				return {
					...e,
					pnl: Math.round(e.pnl * 100) / 100,
					partial: startsLate || endsEarly,
					note: e.year === nowYear
						? 'YTD'
						: startsLate || endsEarly
							? `${monthLabel(e.first)}–${monthLabel(e.last)}`
							: null,
				};
			});
	}, [dailyPnl]);

	if (years.length === 0) return null;

	const total = years.reduce((sum, y) => sum + y.pnl, 0);
	const best = years.reduce((b, y) => (y.pnl > b.pnl ? y : b), years[0]);
	// Average across full years only — a partial year would drag it down.
	const fullYears = years.filter((y) => !y.partial);
	const avgPerYear = fullYears.length
		? fullYears.reduce((s, y) => s + y.pnl, 0) / fullYears.length
		: null;

	const maxAbs = Math.max(...years.map((y) => Math.abs(y.pnl))) || 1;

	return (
		// h-full so it matches the equity curve's height when side by side.
		<Card className="h-full">
			<CardHeader>
				<CardTitle className="text-sm">Annual P&amp;L</CardTitle>
			</CardHeader>
			<CardContent className="flex flex-col justify-between gap-5" style={{ minHeight: '320px' }}>
				<div className="flex flex-1 items-end gap-3 overflow-x-auto pb-1">
					{years.map((y) => {
						// Percentage of the tallest bar, so the row scales with the card
						// rather than being pinned to a fixed pixel height.
						const height = Math.max((Math.abs(y.pnl) / maxAbs) * 100, 2);
						const positive = y.pnl >= 0;
						return (
							<div key={y.year} className="flex h-full min-w-[64px] flex-1 flex-col items-center justify-end gap-1.5">
								<span className="text-xs font-semibold tabular-nums">
									{formatDollars(y.pnl)}
								</span>
								<div
									className={cn(
										'w-full rounded-t-sm',
										positive ? 'bg-emerald-500' : 'bg-red-500',
										y.partial && 'opacity-60',
									)}
									style={{
										height: `${height}%`,
										// Hatching marks a partial year without needing a legend.
										backgroundImage: y.partial
											? 'repeating-linear-gradient(45deg, rgba(255,255,255,.35) 0 4px, transparent 4px 8px)'
											: undefined,
									}}
									title={`${y.year}: ${formatDollars(y.pnl)}${y.note ? ` (${y.note})` : ''}`}
								/>
								<span className="text-xs text-muted-foreground">{y.year}</span>
								{y.note && (
									<span className="text-[10px] text-amber-600">{y.note}</span>
								)}
							</div>
						);
					})}
				</div>

				<div className="grid grid-cols-3 gap-4 border-t pt-4 text-center">
					<div>
						<div className={cn(
							'text-xl font-bold tabular-nums',
							total >= 0 ? 'text-green-600' : 'text-red-600',
						)}>
							{formatDollars(total)}
						</div>
						<div className="mt-0.5 text-xs text-muted-foreground">Total P&amp;L</div>
					</div>
					<div>
						<div className="text-xl font-bold tabular-nums">
							{avgPerYear == null ? '—' : formatDollars(avgPerYear)}
						</div>
						<div className="mt-0.5 text-xs text-muted-foreground">
							{avgPerYear == null ? 'No full year yet' : `Avg. of ${fullYears.length} full year${fullYears.length === 1 ? '' : 's'}`}
						</div>
					</div>
					<div>
						<div className="text-xl font-bold tabular-nums text-green-600">
							{formatDollars(best.pnl)}
						</div>
						<div className="mt-0.5 text-xs text-muted-foreground">Best year ({best.year})</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

/* ─── Sortable Table Header ──────────────────────────────── */
function SortableHead({ label, sortKey, sort, onSort, className = '' }) {
	const isActive = sort.key === sortKey;
	return (
		<TableHead
			className={cn('cursor-pointer select-none hover:text-foreground', className)}
			onClick={() => onSort(sortKey)}
		>
			<span className="inline-flex items-center gap-1">
				{label}
				{isActive ? (
					sort.dir === 'asc' ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />
				) : (
					<ArrowUpDown className="size-3 opacity-30" />
				)}
			</span>
		</TableHead>
	);
}

/* ─── Main Component ─────────────────────────────────────── */
export const OptionsPerformance = () => {
	const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();

	const [permissionsLoaded, setPermissionsLoaded] = useState(false);

	// Data
	const [metrics, setMetrics] = useState(null);
	const [trades, setTrades] = useState(null);
	const [truncated, setTruncated] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	// Filters
	const today = new Date();
	// Default to the full history — the first backtested trade. A trailing window
	// would open on live-only data and hide the backtest years entirely.
	const [startDate, setStartDate] = useState(HISTORY_START_DATE);
	const [endDate, setEndDate] = useState(formatDateForInput(today));
	const [filterAlgos, setFilterAlgos] = useState([]); // array of algoName strings
	// Restate historical P&L at the user's own sizing, per algo. A real config
	// mixes tickers across algos (Phoenix on SPX alongside Good Morning on XSP),
	// so contracts and ticker are both per-algo. Blank = as-traded.
	// Shape: { MarketPower: { contracts: '2', ticker: 'SPX' }, ... }
	const [algoSizing, setAlgoSizing] = useState({});

	// Income-target planner: type a monthly goal, get the recommended config.
	const [incomeTarget, setIncomeTarget] = useState('');

	// Sorting
	const [sortByAlgo, setSortByAlgo] = useState({ key: 'netPnl', dir: 'desc' });
	const [sortByTrade, setSortByTrade] = useState({ key: 'closeTime', dir: 'desc' });

	const toggleSort = (setter, key) => {
		setter((prev) => ({
			key,
			dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
		}));
	};

	const sortRows = (rows, { key, dir }) => {
		return [...rows].sort((a, b) => {
			const av = a[key] ?? '';
			const bv = b[key] ?? '';
			if (typeof av === 'string' && typeof bv === 'string') {
				return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
			}
			return dir === 'asc' ? av - bv : bv - av;
		});
	};

	// Load permissions
	useEffect(() => {
		const loadPermissions = async () => {
			if (isAuthenticated && user?.sub) {
				const token = await getAccessTokenSilently();
				const userId = user.sub.split('|')[1];
				await getUserPermissions(token, userId);
				setPermissionsLoaded(true);
			}
		};
		loadPermissions();
	}, [isAuthenticated, user, getAccessTokenSilently]);

	// The picker is built from the user's own entitlements — no extra request.
	// The API enforces the same list server-side.
	const availableAlgos = useMemo(() => {
		if (!permissionsLoaded) return [];
		const entitlements = getUserAlgorithms();
		return OPTIONS_ALGOS.filter((a) => entitlements.includes(a.entitlement));
	}, [permissionsLoaded]);

	if (!permissionsLoaded) {
		return (
			<div className="flex items-center justify-center" style={{ minHeight: '200px' }}>
				<Loader2 className="size-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (!hasTenant(TENANT_SMITHFIELD)) {
		return <Navigate to="/connect" replace />;
	}

	// Algos the sizing popover applies to: the current selection, or all
	// available when nothing is filtered.
	const sizingTargets = filterAlgos.length > 0
		? availableAlgos.filter((a) => filterAlgos.includes(a.algoName))
		: availableAlgos;

	// "Algo:contracts:ticker,..." — omitted entirely when nothing is overridden.
	// Takes its inputs explicitly so a caller can build the param from values it
	// just computed, without waiting for a state update to land.
	const buildSizingParam = (sizing, targets) =>
		targets
			.map(({ algoName }) => {
				const cfg = sizing[algoName] || {};
				if (!cfg.contracts && !cfg.ticker) return null;
				return `${algoName}:${cfg.contracts || ''}:${cfg.ticker || ''}`;
			})
			.filter(Boolean)
			.join(',');

	const sizingParam = buildSizingParam(algoSizing, sizingTargets);

	const hasSizing = sizingParam.length > 0;

	const setSizing = (algoName, field, value) =>
		setAlgoSizing((prev) => ({
			...prev,
			[algoName]: { ...prev[algoName], [field]: value },
		}));

	// Only the prepared tiers are selectable — no interpolation between them.
	// Keyed by `id` rather than `target`, since two tiers can share a monthly
	// target with different configs (e.g. a conservative variant).
	const tierId = (t) => t.id || String(t.target);
	const recommendedTier = INCOME_TIERS.find((t) => tierId(t) === incomeTarget) || null;

	const applyTier = (tier) => {
		if (!tier) return;
		const next = {};
		for (const [algoName, cfg] of Object.entries(tier.config)) {
			next[algoName] = { contracts: String(cfg.contracts), ticker: cfg.ticker };
		}
		// Restrict the view to the algos this tier actually uses.
		const tierAlgos = availableAlgos
			.filter((a) => tier.config[a.algoName])
			.map((a) => a.algoName);

		setAlgoSizing(next);
		setFilterAlgos(tierAlgos);

		// Fetch with the values we just built rather than the state we just set —
		// applying a configuration should show its results, not wait for a
		// second click on Apply.
		const tierTargets = availableAlgos.filter((a) => tierAlgos.includes(a.algoName));
		fetchPerformance({
			algos: tierAlgos,
			sizing: buildSizingParam(next, tierTargets),
		});
	};

	const describeConfig = (config) =>
		Object.entries(config)
			.map(([algoName, cfg]) => {
				const label = OPTIONS_ALGOS.find((a) => a.algoName === algoName)?.label || algoName;
				return `${cfg.contracts} ${label} ${cfg.ticker}`;
			})
			.join(' + ');

	const getAuthHeaders = async () => {
		const token = await getAccessTokenSilently();
		const userId = user.sub.split('|')[1];
		return {
			headers: { Authorization: `Bearer ${token}` },
			params: { uid: userId },
		};
	};

	// Overrides let a caller fetch with values it just computed — React batches
	// state updates, so reading filterAlgos/sizingParam straight after setState
	// would send the previous selection.
	const fetchPerformance = async (overrides = {}) => {
		const algos = overrides.algos ?? filterAlgos;
		const sizing = overrides.sizing ?? sizingParam;

		setLoading(true);
		setError(null);
		try {
			const config = await getAuthHeaders();
			const queryParams = {
				...config.params,
				startDate,
				endDate,
				...(algos.length > 0 && { algos: algos.join(',') }),
				...(sizing && { sizing }),
			};
			const [metricsRes, tradesRes] = await Promise.all([
				axios.get(`${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/options/metrics`,
					{ headers: config.headers, params: queryParams }),
				axios.get(`${process.env.REACT_APP_SERVERLESS_API_URL}/api/v2/options/trades`,
					{ headers: config.headers, params: queryParams }),
			]);
			setMetrics(metricsRes.data?.data || metricsRes.data);
			setTrades(tradesRes.data?.trades || []);
			setTruncated(!!tradesRes.data?.truncated);
		} catch (err) {
			setError(err.response?.data?.error || err.response?.data || err.message);
		} finally {
			setLoading(false);
		}
	};

	const toggleAlgo = (algoName) => {
		setFilterAlgos((prev) =>
			prev.includes(algoName) ? prev.filter((v) => v !== algoName) : [...prev, algoName]
		);
	};

	// Derived data
	const summary = metrics?.summary || {};
	const dailyPnl = metrics?.dailyPnl || [];
	const pnlByAlgo = metrics?.pnlByAlgo || [];

	const sortedDailyPnl = [...dailyPnl].sort((a, b) => a.date.localeCompare(b.date));
	const cumulativePnl = buildCumulativePnl(sortedDailyPnl);

	const hasMetrics = summary.totalTrades > 0;
	const netPnlVariant = (summary.netPnl || 0) >= 0 ? 'profit' : 'loss';

	// Equity curve takes its colour from where the curve ends up, so a losing
	// period reads as red at a glance rather than needing the axis checked.
	const endingEquity = cumulativePnl.length
		? cumulativePnl[cumulativePnl.length - 1].cumulative
		: 0;
	const curveColor = endingEquity >= 0 ? 'hsl(var(--profit))' : 'hsl(var(--loss))';

	// Explain which part of the range is simulated vs actually traded. Phrased
	// against the dates the user picked, so it only mentions a split when their
	// range actually spans one.
	const liveStart = metrics?.dataSources?.liveStartDate;
	const sourceNote = (() => {
		if (!liveStart || !metrics) return null;
		const readable = (d) => {
			const parsed = new Date(`${d}T00:00:00`);
			return isNaN(parsed.getTime())
				? d
				: parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
		};
		const allBacktest = endDate < liveStart;
		const allLive = startDate >= liveStart;

		if (allBacktest) {
			return `These results are simulated from historical data — this range predates live trading, which began ${readable(liveStart)}.`;
		}
		if (allLive) {
			return 'These results are from trades actually placed in the market.';
		}
		return `Results before ${readable(liveStart)} are simulated from historical data; from ${readable(liveStart)} onward they are trades actually placed in the market. Each strategy switches on its own go-live date, and each trade is counted once.`;
	})();

	// Per-algo go-live dates, shown when the selected range spans them so it's
	// clear a strategy contributes simulated results for part of the window.
	const algoLiveStarts = metrics?.dataSources?.byAlgo || [];

	const heading = hasTenant(TENANT_INVESTCEO) ? 'Options Performance' : 'Performance';

	return (
		<div className="alm-metrics space-y-4 p-4 md:p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
			<div className="flex items-center gap-4 mb-2">
				<h1 className="mr-2 text-xl font-bold text-foreground">{heading}</h1>
			</div>

			{/* ── Toolbar ───────────────────────────────────── */}
			<div className="flex flex-wrap items-center gap-2 mb-4">
				{availableAlgos.length > 0 && (
					<Popover>
						<PopoverTrigger asChild>
							<Button variant="outline" size="sm" className="h-8 w-[180px] justify-between text-xs">
								<span className="truncate">
									{filterAlgos.length === 0
										? 'All algos'
										: `${filterAlgos.length} selected`}
								</span>
								<ChevronDown className="ml-1.5 size-3 shrink-0" />
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-60 p-2" align="start">
							<div className="max-h-72 space-y-0.5 overflow-y-auto">
								{availableAlgos.map((a) => (
									<label
										key={a.algoName}
										className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent"
									>
										<Checkbox
											checked={filterAlgos.includes(a.algoName)}
											onCheckedChange={() => toggleAlgo(a.algoName)}
										/>
										{a.label}
									</label>
								))}
							</div>
						</PopoverContent>
					</Popover>
				)}

				<Input type="date" className="h-8 w-[140px] text-xs" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
				<Input type="date" className="h-8 w-[140px] text-xs" value={endDate} onChange={(e) => setEndDate(e.target.value)} />

				{sizingTargets.length > 0 && (
					<Popover>
						<PopoverTrigger asChild>
							<Button variant="outline" size="sm" className="h-8 text-xs">
								Per-Algo Sizing
								{hasSizing && <span className="ml-1 text-muted-foreground">*</span>}
							</Button>
						</PopoverTrigger>
						<PopoverContent className="w-80 p-3" align="start">
							<p className="text-xs text-muted-foreground mb-2">
								Restate P&amp;L at your own sizing. Blank uses the size and ticker
								actually traded. XSP is 1/10 the size of SPX.
							</p>
							<div className="space-y-2">
								{sizingTargets.map(({ algoName, label }) => (
									<div key={algoName} className="flex items-center gap-2">
										<span className="text-xs flex-1 truncate">{label}</span>
										<Input
											type="number"
											className="h-7 w-[70px] text-xs"
											placeholder="Qty"
											value={algoSizing[algoName]?.contracts || ''}
											onChange={(e) => setSizing(algoName, 'contracts', e.target.value)}
											min="1"
										/>
										<select
											className="h-7 w-[90px] rounded-md border border-input bg-background px-1 text-xs"
											value={algoSizing[algoName]?.ticker || ''}
											onChange={(e) => setSizing(algoName, 'ticker', e.target.value)}
										>
											<option value="">As traded</option>
											<option value="SPX">SPX</option>
											<option value="XSP">XSP</option>
										</select>
									</div>
								))}
							</div>
						</PopoverContent>
					</Popover>
				)}

				<Popover>
					<PopoverTrigger asChild>
						<Button variant="outline" size="sm" className="h-8 text-xs">
							<Target className="mr-1 size-3" />
							Income Planner
						</Button>
					</PopoverTrigger>
					<PopoverContent className="w-96 p-3" align="start">
						<p className="text-xs text-muted-foreground mb-2">
							Pick a monthly income goal to see the recommended configuration and
							the account size it assumes.
						</p>
						<select
							className="mb-3 h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
							value={incomeTarget}
							onChange={(e) => setIncomeTarget(e.target.value)}
						>
							<option value="">Select a monthly target…</option>
							{INCOME_TIERS.map((t) => (
								<option key={tierId(t)} value={tierId(t)}>
									{formatDollars(t.target)}/mo{t.label ? ` ${t.label}` : ''} — min {formatDollars(t.minCapital)}
								</option>
							))}
						</select>

						{recommendedTier && (
							<div className="space-y-2 rounded-md border bg-muted/40 p-2.5">
								<div className="flex items-baseline justify-between">
									<span className="text-xs font-medium">
										{formatDollars(recommendedTier.target)}/mo
										{recommendedTier.label ? ` ${recommendedTier.label}` : ''}
									</span>
									<span className="text-xs text-muted-foreground">
										min {formatDollars(recommendedTier.minCapital)}
									</span>
								</div>
								<div className="text-xs">{describeConfig(recommendedTier.config)}</div>
								<Button
									size="sm"
									className="h-7 w-full text-xs"
									onClick={() => applyTier(recommendedTier)}
								>
									Apply this configuration
								</Button>
							</div>
						)}

						<p className="mt-2 text-[11px] leading-snug text-muted-foreground">
							Targets are planning estimates from the published templates, not a
							forecast. Apply a configuration to see how it would have performed
							over your selected date range.
						</p>
					</PopoverContent>
				</Popover>

				<Button size="sm" className="h-8 text-xs" onClick={fetchPerformance} disabled={loading}>
					{loading ? <Loader2 className="mr-1 size-3 animate-spin" /> : <Search className="mr-1 size-3" />}
					{loading ? 'Loading...' : 'Apply'}
				</Button>
			</div>

			{/* ── Content ───────────────────────────────────── */}
			{error && (
				<div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
					{typeof error === 'string' ? error : 'Error loading performance data'}
				</div>
			)}

			{loading ? (
				<div className="flex items-center justify-center py-20">
					<Loader2 className="size-8 animate-spin text-muted-foreground" />
				</div>
			) : hasMetrics ? (
				<div className="space-y-6">
					{/* Standing disclaimer plus, when the API supplies it, a plain
					    statement of which part of the series is simulated. The curve
					    switches source mid-stream and a seamless chart would otherwise
					    imply all of it is real trading. */}
					<div className="space-y-1.5 rounded-lg border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
						{sourceNote && <p className="m-0">{sourceNote}</p>}
						{algoLiveStarts.length > 0 && (
							<p className="m-0">
								Live start by strategy:{' '}
								{algoLiveStarts
									.map((a) => `${a.algo} ${new Date(`${a.liveStart}T00:00:00`)
										.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`)
									.join(' · ')}
							</p>
						)}
						<p className="m-0">
							Past performance is not indicative of future results. Simulated
							results have inherent limitations and do not reflect the impact of
							slippage, fees, or liquidity constraints on actual trades. Figures
							scaled to a contract count or ticker you selected are illustrative
							and do not represent trades placed at that size. Options trading
							involves substantial risk of loss and is not suitable for every
							investor.
						</p>
					</div>

					{hasSizing && (
						<div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
							P&amp;L is scaled to your configured sizing (
							{sizingTargets
								.filter(({ algoName }) => algoSizing[algoName]?.contracts || algoSizing[algoName]?.ticker)
								.map(({ algoName, label }) => {
									const cfg = algoSizing[algoName];
									return [cfg.contracts, label, cfg.ticker].filter(Boolean).join(' ');
								})
								.join(', ')}
							), so figures differ from the size each trade was actually placed at.
							Per-trade multipliers (such as Good Morning double-downs) still scale
							off your contract count.
						</div>
					)}
					{/* Sits directly above the numbers it qualifies: these totals sum
					    a fixed position size across the whole range, so they are not a
					    compounded return and shouldn't be read as one. */}
					<div className="rounded-lg bg-red-600 px-4 py-2.5 text-center text-sm font-bold text-white">
						Results Are NOT Compounded. Contract Size is Held Constant Through The
						Selected Time Frame.
					</div>

					{/* ── Headline KPIs ──────────────────────── */}
					{/* The three a reader checks first: what it made, how often it
					    won, and how much it risked to get there. */}
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
						<KpiCard
							title="Net P&L"
							value={formatDollars(summary.netPnl)}
							subtitle={`${summary.totalTrades || 0} trades`}
							icon={summary.netPnl >= 0 ? TrendingUp : TrendingDown}
							variant={netPnlVariant}
						/>
						<KpiCard
							title="Win Rate"
							value={formatPct(summary.winRate)}
							subtitle={`${summary.wins || 0}W / ${summary.losses || 0}L`}
							icon={Target}
						/>
						{/* Drawdown reads as a percentage of the peak it fell from — a raw
						    dollar figure means little without the account size behind it. */}
						<KpiCard
							title="Max Drawdown"
							value={summary.maxDrawdownPct == null
								? formatDollars(summary.maxDrawdown)
								: `${summary.maxDrawdownPct.toFixed(1)}%`}
							subtitle={summary.maxDrawdownPct == null
								? 'Peak to trough'
								: `${formatDollars(summary.maxDrawdown)} peak to trough`}
							icon={TrendingDown}
							variant="caution"
						/>
					</div>

					{/* ── Equity Curve + Annual P&L ───────────── */}
					<div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
					<Card>
						<CardHeader>
							<CardTitle className="text-sm">Cumulative P&L</CardTitle>
						</CardHeader>
						<CardContent>
							<ChartContainer
								config={{ cumulative: { label: 'Cumulative P&L', color: curveColor } }}
								className="h-[320px]"
							>
								<AreaChart data={cumulativePnl}>
									<defs>
										{/* Shading follows the ending value: green while the curve
										    is net positive, red once it's underwater. */}
										<linearGradient id="equityFill" x1="0" y1="0" x2="0" y2="1">
											<stop offset="0%" stopColor={curveColor} stopOpacity={0.32} />
											<stop offset="100%" stopColor={curveColor} stopOpacity={0.02} />
										</linearGradient>
									</defs>
									<CartesianGrid strokeDasharray="3 3" vertical={false} />
									<XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={32} />
									<YAxis tick={{ fontSize: 11 }} width={72} tickFormatter={(v) => formatDollars(v)} />
									<ChartTooltip content={<ChartTooltipContent formatter={(v) => formatDollars(v)} />} />
									<ReferenceLine y={0} stroke="hsl(var(--border))" />
									<Area
										type="monotone"
										dataKey="cumulative"
										stroke={curveColor}
										strokeWidth={2}
										fill="url(#equityFill)"
										dot={false}
										activeDot={{ r: 4 }}
									/>
								</AreaChart>
							</ChartContainer>
						</CardContent>
					</Card>

					<AnnualReturns dailyPnl={sortedDailyPnl} />
					</div>

					{/* ── Secondary KPIs ─────────────────────── */}
					{/* 3x2: the detail a reader goes to after the headline numbers. */}
					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
						<KpiCard
							title="Profit Factor"
							value={(summary.profitFactor || 0).toFixed(2)}
							subtitle="Gross profit / gross loss"
							icon={Activity}
						/>
						<KpiCard
							title="Expectancy"
							value={formatDollars(summary.expectancy)}
							subtitle="Expected P&L per trade"
							icon={Scale}
							variant={(summary.expectancy || 0) >= 0 ? 'profit' : 'loss'}
						/>
						<KpiCard
							title="Sharpe Ratio"
							value={summary.sharpe == null ? '—' : summary.sharpe.toFixed(2)}
							subtitle="Annualized, 0% risk-free"
							icon={Gauge}
						/>
						<KpiCard
							title="Avg Monthly"
							value={formatDollars(summary.avgMonthlyPnl)}
							subtitle="Per month traded"
							icon={CalendarDays}
							variant={(summary.avgMonthlyPnl || 0) >= 0 ? 'profit' : 'loss'}
						/>
						<KpiCard
							title="Avg Yearly"
							value={formatDollars(summary.avgYearlyPnl)}
							subtitle="Annualized from monthly"
							icon={CalendarDays}
							variant={(summary.avgYearlyPnl || 0) >= 0 ? 'profit' : 'loss'}
						/>
						{/* Sits in the grid as a tile rather than a full-width card, so the
						    row completes 3x2 instead of leaving a gap. */}
						<Card className="gap-4">
							<CardHeader className="pb-0">
								<CardTitle className="flex items-center text-sm text-muted-foreground">
									<Scale className="mr-2 size-4" />
									Avg Win / Loss
								</CardTitle>
							</CardHeader>
							<CardContent className="flex h-full flex-col justify-center">
								<WinLossBars avgWin={summary.avgWin} avgLoss={summary.avgLoss} />
							</CardContent>
						</Card>
					</div>

					{/* ── Calendar & Period Tiles ─────────────── */}
					<CalendarHeatmap data={sortedDailyPnl} />

					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						<WeeklyPnlTiles data={sortedDailyPnl} />
						<MonthlyPnlTiles data={sortedDailyPnl} />
					</div>

					{/* ── Breakdown by Algo ────────────────────── */}
					{pnlByAlgo.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">P&amp;L by Algorithm</CardTitle>
							</CardHeader>
							<CardContent>
								<Table>
									<TableHeader>
										<TableRow>
											<SortableHead label="Algorithm" sortKey="algo" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} />
											<SortableHead label="Trades" sortKey="trades" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
											<SortableHead label="Wins" sortKey="wins" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
											<SortableHead label="Losses" sortKey="losses" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
											<SortableHead label="Win %" sortKey="winRate" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
											<SortableHead label="Avg Win" sortKey="avgWin" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
											<SortableHead label="Avg Loss" sortKey="avgLoss" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
											<SortableHead label="Net P&L" sortKey="netPnl" sort={sortByAlgo} onSort={(k) => toggleSort(setSortByAlgo, k)} className="text-right" />
										</TableRow>
									</TableHeader>
									<TableBody>
										{sortRows(pnlByAlgo, sortByAlgo).map((row, idx) => (
											<TableRow key={row.algo || idx}>
												<TableCell className="font-medium">{row.algo}</TableCell>
												<TableCell className="text-right">{row.trades}</TableCell>
												<TableCell className="text-right text-emerald-600">{row.wins}</TableCell>
												<TableCell className="text-right text-red-600">{row.losses}</TableCell>
												<TableCell className="text-right">{formatPct(row.winRate)}</TableCell>
												<TableCell className="text-right">{formatDollars(row.avgWin)}</TableCell>
												<TableCell className="text-right">{formatDollars(row.avgLoss)}</TableCell>
												<TableCell className="text-right">
													<Badge variant={row.netPnl >= 0 ? 'profit' : 'loss'}>
														{formatDollars(row.netPnl)}
													</Badge>
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					)}

					{/* ── Trade List ───────────────────────────── */}
					{trades?.length > 0 && (
						<Card>
							<CardHeader>
								<CardTitle className="text-sm">
									Trades
									<span className="ml-2 font-normal text-muted-foreground">
										({trades.length}{truncated ? ' shown — narrow the date range to see the rest' : ''})
									</span>
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="overflow-x-auto">
									<Table>
										<TableHeader>
											<TableRow>
												<SortableHead label="Closed" sortKey="closeTime" sort={sortByTrade} onSort={(k) => toggleSort(setSortByTrade, k)} />
												<SortableHead label="Algorithm" sortKey="algo" sort={sortByTrade} onSort={(k) => toggleSort(setSortByTrade, k)} />
												<SortableHead label="Ticker" sortKey="ticker" sort={sortByTrade} onSort={(k) => toggleSort(setSortByTrade, k)} />
												<SortableHead label="Contracts" sortKey="contracts" sort={sortByTrade} onSort={(k) => toggleSort(setSortByTrade, k)} className="text-right" />
												<SortableHead label="Status" sortKey="status" sort={sortByTrade} onSort={(k) => toggleSort(setSortByTrade, k)} />
												<SortableHead label="P&L" sortKey="pnl" sort={sortByTrade} onSort={(k) => toggleSort(setSortByTrade, k)} className="text-right" />
											</TableRow>
										</TableHeader>
										<TableBody>
											{sortRows(trades, sortByTrade).map((t, idx) => (
												<TableRow key={t.tradeKey || idx}>
													<TableCell className="whitespace-nowrap">{formatDateTime(t.closeTime || t.detectionTime)}</TableCell>
													<TableCell className="font-medium">{t.algo}</TableCell>
													<TableCell>{t.ticker}</TableCell>
													<TableCell className="text-right">{t.contracts}</TableCell>
													<TableCell>{t.status}</TableCell>
													<TableCell className="text-right">
														{t.pnl == null ? (
															<span className="text-muted-foreground">—</span>
														) : (
															<Badge variant={t.pnl >= 0 ? 'profit' : 'loss'}>
																{formatDollars(t.pnl)}
															</Badge>
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							</CardContent>
						</Card>
					)}
				</div>
			) : metrics ? (
				/* No data state */
				<div className="py-16 text-center">
					<BarChart3 className="mx-auto mb-4 size-12 opacity-40" />
					<h3 className="mb-2 text-lg font-medium text-foreground">No Trades Found</h3>
					<p className="text-sm text-muted-foreground">
						No trades found for the selected date range and algorithms.
					</p>
				</div>
			) : (
				/* Initial state — no query yet */
				<div className="py-16 text-center">
					<BarChart3 className="mx-auto mb-4 size-12 opacity-40" />
					<h3 className="mb-2 text-lg font-medium text-foreground">Options Performance</h3>
					<p className="text-sm text-muted-foreground">
						Select a date range and click Apply to view your trading performance.
					</p>
				</div>
			)}
		</div>
	);
};